import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    inquiryType: "trade",
    message: "",
    website_url: "", // Honeypot (Hidden)
    form_start_time: Date.now() // Time trap
  });
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const nameInputRef = useRef(null);

  // Reset timer on mount
  useEffect(() => {
     setFormData(prev => ({ ...prev, form_start_time: Date.now() }));
  }, []);

  useEffect(() => {
    if (location.state?.focusTrade && nameInputRef.current) {
       nameInputRef.current.focus();
       nameInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/trade-inquiry`, formData);
      toast.success("Inquiry Sent", {
        description: "Thank you. Our trade team will be in touch shortly.",
      });
      // Reset form but keep timer fresh
      setFormData({ 
          name: "", 
          email: "", 
          company: "", 
          inquiryType: "trade", 
          message: "", 
          website_url: "",
          form_start_time: Date.now() 
      });
    } catch (error) {
      console.error(error);
      toast.error("Submission Failed", {
        description: error.response?.data?.detail || "Please try again later or email us directly.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dreibach-black min-h-screen pb-24">
      <PageHeader 
        title="Contact & Trade" 
        subtitle="Partner with the logistics engine behind the UK's leading brands."
        image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000"
      />

      <div className="container mx-auto px-6 py-12 max-w-5xl">
        
        {/* Main Focus: B2B Partnership */}
        <div className="bg-black/50 p-8 md:p-16 border border-white/10 mb-8 relative overflow-hidden">
             {/* Gradient Accent */}
             <div className="absolute top-0 right-0 w-64 h-64 bg-dreibach-teal/5 blur-[100px] rounded-full pointer-events-none"></div>

             <div className="mb-6 text-center">
                <span className="inline-block py-1 px-3 bg-dreibach-teal/20 text-dreibach-teal text-xs font-bold uppercase tracking-widest mb-3">B2B Partnership</span>
                <h2 className="heading-md text-white mb-3">Supplier or Retail Partner?</h2>
                <p className="text-gray-400 max-w-xl mx-auto">
                    Apply for a trade account or discuss supply chain opportunities. Direct access to our procurement team.
                </p>
             </div>

             <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
                
                {/* Honeypot Field (Hidden) */}
                <div className="hidden" aria-hidden="true">
                    <label htmlFor="website_url">Website</label>
                    <input 
                        type="text" 
                        id="website_url" 
                        name="website_url" 
                        value={formData.website_url} 
                        onChange={handleChange} 
                        tabIndex="-1" 
                        autoComplete="off"
                    />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                   <div className="space-y-2">
                      <Input 
                          ref={nameInputRef}
                          name="name" 
                          value={formData.name} 
                          onChange={handleChange} 
                          required 
                          className="bg-dreibach-card border-white/10 text-white focus:border-dreibach-teal rounded-none h-12 text-xs tracking-wider"
                          placeholder="Full Name *"
                      />
                   </div>
                   <div className="space-y-2">
                      <Input 
                          name="company" 
                          value={formData.company} 
                          onChange={handleChange} 
                          className="bg-dreibach-card border-white/10 text-white focus:border-dreibach-teal rounded-none h-12 text-xs tracking-wider"
                          placeholder="Company Name"
                      />
                   </div>
                </div>

                <div className="space-y-2">
                   <Input 
                      name="email" 
                      type="email"
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      className="bg-dreibach-card border-white/10 text-white focus:border-dreibach-teal rounded-none h-12 uppercase text-xs tracking-wider placeholder:normal-case"
                      placeholder="Business Email *"
                   />
                </div>

                <div className="space-y-2">
                   <Textarea 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      required 
                      className="bg-dreibach-card border-white/10 text-white focus:border-dreibach-teal rounded-none min-h-[120px] text-xs tracking-wider"
                      placeholder="Tell us about your requirements... *"
                   />
                </div>

                <div className="pt-4">
                    <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-dreibach-teal hover:bg-white hover:text-black text-white uppercase font-bold tracking-widest h-14 rounded-none transition-all"
                    >
                    {loading ? "Sending..." : "Submit Trade Inquiry"}
                    </Button>
                </div>
                
                <div className="flex items-center justify-center gap-2 mt-4 text-center">
                    <MapPin className="text-gray-500 w-4 h-4" />
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                        Head Office: Borehamwood, WD6 1GW
                    </p>
                </div>
              </form>
        </div>

        {/* Secondary: Customer Support */}
        <div className="bg-dreibach-card p-8 md:p-12 border border-white/5 flex flex-col md:flex-row items-center justify-between gap-8">
             <div className="text-center md:text-left">
                <span className="inline-block py-1 px-3 bg-white/5 text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-3">Support</span>
                <h3 className="text-2xl font-bold text-white uppercase mb-2">Customer Queries?</h3>
                <p className="text-gray-500 text-sm max-w-md">
                    Bought one of our products? Our dedicated support team is here to help with any product queries.
                </p>
             </div>

             <div className="flex flex-col sm:flex-row gap-8">
                <div className="flex items-center gap-4">
                   <div className="w-10 h-10 bg-white/5 flex items-center justify-center rounded-full">
                        <Mail className="text-dreibach-teal w-5 h-5" />
                   </div>
                   <div>
                      <h4 className="text-white text-xs font-bold uppercase tracking-wide mb-0.5">Email Us</h4>
                      <a href="mailto:customersupport@dreibach.co.uk" className="text-gray-400 hover:text-white transition-colors text-sm">customersupport@dreibach.co.uk</a>
                   </div>
                </div>
                
                <div className="flex items-center gap-4">
                   <div className="w-10 h-10 bg-white/5 flex items-center justify-center rounded-full">
                        <Clock className="text-dreibach-teal w-5 h-5" />
                   </div>
                   <div>
                      <h4 className="text-white text-xs font-bold uppercase tracking-wide mb-0.5">Hours</h4>
                      <p className="text-gray-400 text-sm">Mon-Fri, 09:00 - 17:00</p>
                   </div>
                </div>
             </div>
        </div>

      </div>
    </div>
  );
};


export default Contact;
